<template>
  <div class="controls">
    <div class="controls__start">
      <span v-if="isLoading" class="controls__count">Experiments</span>
      <span v-else class="controls__count">{{ `Experiments (${experiments.length})` }}</span>
    </div>

    <HelpTip>
      Model experiment contains training sessions and validate sessions.
      The experiments allow user to compare the results of all sessions within.
      Click on Training Sessions or Validate Sessions to navigate to the corresponding dashboard.
    </HelpTip>

    <HelpTip v-if="permissions && !permissions.trainer_write" class="ml-1" help_style="alert">
      This is a read only experiment. Editing functionalities are disabled.
      To train a model using datasets in this project, create a local project, copy datasets to the local project and train using copied datasets.
    </HelpTip>

    <div class="controls__end">
      <div class="controls__actions">
        <button
          v-if="permissions && permissions.trainer_write"
          id="new-experiment-btn"
          class="button button-sm"
          @click="openCreateModal"
        >
          New Experiment
        </button>
      </div>
    </div>
  </div>
  <div v-if="isLoading" id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  <div v-else-if="!isLoading && experiments.length === 0" class="empty-state">
    <EmptyState
      :title="'No experiments found for this project'"
      :subtitle="'To add a new experiment, click on NEW EXPERIMENT'"
    />
  </div>
  <div v-else-if="!isLoading && experiments.length > 0" class="manager-card-container scrollbar">
    <ExperimentCard
      v-for="e in experiments.sort((a,b) => b.id - a.id)"
      :id="e.id"
      :key="e.id"
      :name="e.name"
      :description="e.description"
      :running="e.no_running"
      :complete="e.no_completed"
      :terminated="e.no_terminated"
      :valRunning="e.val_running"
      :valError="e.val_error"
      :valCompleted="e.val_completed"
      :valTerminated="e.val_terminated"
      :errors="e.no_error"
      @delete-experiment="openConfirmDeleteTrainerModal(e)"
      @train-clicked="handleTrainClicked(e)"
      @val-clicked="handleValClicked(e)"
      @edit-experiment="openEditModal(e)"
    />
  </div>

  <CreateTrainerModal
    ref="createEditTrainerModal"
    :project="currentProject"
    @trainer-created="handleTrainerCreated"
  />
  <TrainerUpdateModal
    ref="trainerUpdateModal"
    :modalTrainer="modalTrainer"
    @edit-trainer="handleUpdateTrainer"
  />

  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
</template>

<script>
import { inject } from 'vue';
import ExperimentCard from '@/components/TrainerComponent/TrainerExperimentAndSessions/ExperimentCard.vue';

import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import EmptyState from '@/components/EmptyState.vue';
import TrainerUpdateModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainerUpdateModal.vue';
import CreateTrainerModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/CreateTrainerModal.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import HelpTip from '../../HelpTip.vue';

const $toast = useToast();

export default {
  name: "TrainerManagement",
  components: {
    ExperimentCard,
    CreateTrainerModal,
    ConfirmModal,
    TrainerUpdateModal,
    HelpTip,
    EmptyState,
  },
  inject: ['isPublic'],
  props: {
  },
  emits: [
    'trainer-set-created', 'update-list',
  ],
  data() {
    return {
      experiments: [],
      isLoading: true,

      confirmMessage: null,
      confirmMessageHeader: null,
      toDelete: null,
      confirmFunction: null,
      buttonText: 'Delete',
      modalTrainer: null,
      permissions: null,
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    currentExperiment: {
      get() {
        return this.$store.state.experiments.currentExperiment;
      },
      set(experiment) {
        this.$store.commit('experiments/setCurrentExperiment', experiment);
      },
    },
    notEmpty() {
      return this.experiments && this.experiments.length > 0;
    },
    trainerIds() {
      return this.experiments.reduce((acc, curr) => {
        acc.push(curr.id);
        return acc;
      }, []);
    },
  },
  watch: {
    async currentProject(p) {
      if (p) {
        this.loadExpList();
      }
    },
  },
  async created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
  },
  mounted() {
    this.getProjectAccessForSingleUser();
    this.loadExpList();
  },
  methods: {
    async loadExpList() {
      this.isLoading = true;
      this.experiments = await this.getTrainerList();
      if (this.experiments.length > 0) {
        this.experiments = this.experiments.sort((a, b) => b.id - a.id);
      }
      this.isLoading = false;
    },
    handleTrainClicked(exp) {
      // await this.$nextTick();
      this.currentExperiment = exp;
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.training.list`, query: { exp_id: exp.id } });
    },
    handleValClicked(exp) {
      // await this.$nextTick();
      this.currentExperiment = exp;
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.validation.list`, query: { exp_id: exp.id } });
    },

    async getProjectAccessForSingleUser() {
      if (this.currentProject && this.$store.state.user?.user?.username) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentProject.id,
          username: this.$store.state.user.user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            }
          })
          .catch((e) => console.log(e));
      }
    },
    openConfirmDeleteTrainerModal(t) {
      this.confirmMessage = `Are you sure you want to remove experiment "${t.name}" to recycle bin? This will also remove all training sessions and validate sessions in progress.`;
      this.confirmMessageHeader = 'Remove Trainer Experiment';
      this.toDelete = t;
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => this.handleTrainerDelete();
    },

    async handleTrainerCreated() {
      this.experiments = await this.getTrainerList();
    },
    async getTrainerList() {
      this.loadingFinished = false;
      const resp = await this.dataConnect.getExperimentList({ project_id: this.currentProject.id })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiments: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        this.loadingFinished = true;
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve experiments: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return [];
      }
      this.loadingFinished = true;
      return resp.result;
    },

    openCreateModal() {
      this.$refs.createEditTrainerModal.showModal();
    },
    openEditModal(e) {
      this.modalTrainer = e;
      this.$refs.trainerUpdateModal.showModal();
    },

    async handleTrainerDelete() {
      await this.dataConnect.deleteTrainer({
        trainer_id: this.toDelete.id,
      })
        .then((data) => {
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to delete experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to delete experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.experiments = await this.getTrainerList();
      this.toDelete = null;
    },

    async handleUpdateTrainer(params) {
      await this.dataConnect.updateTrainer(params)
        .then(async (data) => {
          if (data.result) {
            this.experiments = await this.getTrainerList();
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to update experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to update experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.trainers-card-container {
  display: flex;
  width: 100%;
  padding: 16px;
  overflow-y: scroll;
  flex-wrap: wrap;
}

.trainer-header {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
}

//======================================
.manager-card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  grid-auto-rows: 400px;
  width: 100%;
  gap: 16px;
  padding: 16px;
  overflow-y: auto;
  .list-card {
    height: 100%;
    width: 100%;
    cursor: pointer;
  }
}
</style>
